import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import {
	Box,
	FormControl,
	FormControlLabel,
	Grid,
	InputAdornment,
	InputLabel,
	Radio,
	RadioGroup,
	Switch,
	Typography
} from "@mui/material";
import { FC, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";

import NumberInput from "../../../components/NumberInput";
import { useAppDispatch } from "../../../redux";
import { showFeedbackNotification } from "../../../redux/reducers/feedbackNotification.reducer";
import { useEditSocietyConfigMutation, useGetSocietyConfigQuery } from "../../../redux/reducers/settings.reducer";

enum TAT_DIRECTION_E {
	INSIDE_PREMISES = 0,
	OUTSIDE_PREMISES = 1
}

interface ITATConfig {
	tat_enabled: boolean;
	direction: TAT_DIRECTION_E;
	min_threshold_seconds: string;
	max_threshold_days: string;
	overstay_alert_enabled: boolean;
	overstay_alerts_for_authorized: boolean;
	overstay_duration: string;
	overstay_day_range: string;
}

const defaultTATInputs: ITATConfig = {
	tat_enabled: false,
	direction: TAT_DIRECTION_E.INSIDE_PREMISES,
	min_threshold_seconds: "30",
	max_threshold_days: "7",
	overstay_alert_enabled: false,
	overstay_alerts_for_authorized: false,
	overstay_duration: "10080",
	overstay_day_range: "168"
};

const AdvancedSettings: FC = () => {
	const dispatch = useAppDispatch();

	const {
		reset,
		watch,
		control,
		register,
		handleSubmit,
		formState: { isDirty: inputChanged }
	} = useForm<ITATConfig>({ defaultValues: defaultTATInputs });

	const tatEnabled = watch("tat_enabled");
	const overstayAlertsEnabled = watch("overstay_alert_enabled");

	// APIS
	// GET SOCIETY CONFIG
	const { data: getSocietyConfigResponse, refetch: refetchSocietyConfig } = useGetSocietyConfigQuery(null);

	// EDIT SOCIETY CONFIG
	const [editSocietyConfig, { isLoading: editSocietyConfigLoading, reset: resetEditSocietyConfig }] =
		useEditSocietyConfigMutation();

	const societyConfig = useMemo(
		() =>
			getSocietyConfigResponse && getSocietyConfigResponse.results.length > 0
				? getSocietyConfigResponse.results[0]
				: null,
		[getSocietyConfigResponse]
	);

	function handleSaveButtonClick(formData: ITATConfig) {
		if (societyConfig && societyConfig.tat_config && societyConfig.tat_config.length > 0) {
			editSocietyConfig({
				id: societyConfig.id,
				data: {
					tat_config: [
						{
							tat: {
								enabled: formData.tat_enabled,
								direction: Number(formData.direction),
								min_tat_duration_limit: Number(formData.min_threshold_seconds),
								max_tat_duration_limit: Number(formData.max_threshold_days)
							},
							email_alerts: {
								enabled: formData.overstay_alert_enabled,
								overstay_alerts_for_authorized: formData.overstay_alerts_for_authorized,
								overstay_duration: Number(formData.overstay_duration),
								overstay_day_range: Number(formData.overstay_day_range)
							}
						}
					]
				}
			})
				.unwrap()
				.then(() => {
					dispatch(
						showFeedbackNotification({
							message: "Turn around time changes saved successfully",
							severity: "success"
						})
					);

					resetEditSocietyConfig();
					refetchSocietyConfig();
				})
				.catch((error) => {
					dispatch(
						showFeedbackNotification({
							message: error.data ? String(error.data) : "Failed to save turn around time changes",
							severity: "error"
						})
					);
				});
		}
	}

	useEffect(() => {
		if (societyConfig && societyConfig.tat_config && societyConfig.tat_config.length > 0) {
			const tatConfig = societyConfig.tat_config[0].tat;
			const emailAlertDetails = societyConfig.tat_config[0].email_alerts;
			reset({
				tat_enabled: tatConfig.enabled,
				direction: tatConfig.direction,
				min_threshold_seconds: String(tatConfig.min_tat_duration_limit),
				max_threshold_days: String(tatConfig.max_tat_duration_limit),
				overstay_alert_enabled: emailAlertDetails.enabled,
				overstay_alerts_for_authorized: emailAlertDetails.overstay_alerts_for_authorized ?? false,
				overstay_duration: emailAlertDetails.overstay_duration ? String(emailAlertDetails.overstay_duration) : "10080",
				overstay_day_range: emailAlertDetails.overstay_day_range ? String(emailAlertDetails.overstay_day_range) : "168"
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [societyConfig]);

	return (
		<Box
			className="communication-channels-screen-wrapper"
			component="form"
			noValidate
			onSubmit={handleSubmit(handleSaveButtonClick)}
		>
			<Box className="overstay-alerts-title-wrapper">
				<Typography variant="h6" color="var(--color-primary-main)">
					Turn Around Time
				</Typography>

				<Controller
					control={control}
					name="tat_enabled"
					render={({ field: { value, onChange } }) => <Switch checked={value} onChange={onChange} />}
				/>
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
				<Box>
					<InputLabel>Calculate TAT for</InputLabel>

					<FormControl>
						<Controller
							control={control}
							name="direction"
							render={({ field: { value, onChange } }) => (
								<RadioGroup row value={value} onChange={onChange} sx={{ gap: { xs: "1rem", md: "5rem" } }}>
									<FormControlLabel
										value={TAT_DIRECTION_E.INSIDE_PREMISES}
										control={<Radio />}
										label="Inside Premises"
										disabled={!tatEnabled}
									/>

									<FormControlLabel
										value={TAT_DIRECTION_E.OUTSIDE_PREMISES}
										control={<Radio />}
										label="Outside Premises"
										disabled={!tatEnabled}
									/>
								</RadioGroup>
							)}
						/>
					</FormControl>
				</Box>

				<Box>
					<InputLabel htmlFor="min-threshold-input" disabled={!tatEnabled}>
						TAT Minimum Threshold
					</InputLabel>

					<NumberInput
						fullWidth
						size="small"
						id="min-threshold-input"
						sx={{ marginTop: "0.5rem", maxWidth: "300px" }}
						InputProps={{
							endAdornment: <InputAdornment position="end">seconds</InputAdornment>
						}}
						disabled={!tatEnabled}
						{...register("min_threshold_seconds")}
					/>
				</Box>

				<Box>
					<InputLabel htmlFor="max-threshold-input" disabled={!tatEnabled}>
						TAT Maximum Threshold
					</InputLabel>

					<NumberInput
						fullWidth
						size="small"
						id="max-threshold-input"
						sx={{ marginTop: "0.5rem", maxWidth: "300px" }}
						InputProps={{
							endAdornment: <InputAdornment position="end">days</InputAdornment>
						}}
						disabled={!tatEnabled}
						{...register("max_threshold_days")}
					/>
				</Box>
			</Box>

			<Box className="overstay-alerts-title-wrapper">
				<Typography variant="h6" color="var(--color-primary-main)">
					Overstay Alerts
				</Typography>

				<Controller
					control={control}
					name="overstay_alert_enabled"
					render={({ field: { value, onChange } }) => (
						<Switch checked={value} onChange={onChange} disabled={!tatEnabled} />
					)}
				/>
			</Box>

			<Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: "1rem" }}>
				<FormControlLabel
					control={
						<Controller
							control={control}
							name="overstay_alerts_for_authorized"
							render={({ field: { value, onChange } }) => (
								<Switch checked={value} onChange={onChange} disabled={!overstayAlertsEnabled || !tatEnabled} />
							)}
						/>
					}
					label="Alerts for authorized visitors"
					disabled={!overstayAlertsEnabled || !tatEnabled}
				/>
			</Box>

			<Grid container spacing={2}>
				<Grid item xs={12} md={3}>
					<InputLabel
						htmlFor="duration-input"
						disabled={!overstayAlertsEnabled || !tatEnabled}
						sx={{ color: "#3d3d3d", marginBottom: "0.5rem" }}
					>
						Duration
					</InputLabel>

					<NumberInput
						fullWidth
						positiveOnly
						disableSigned
						disableDecimal
						size="small"
						variant="outlined"
						id="duration-input"
						placeholder="Enter duration"
						maxValue={10080}
						disabled={!overstayAlertsEnabled || !tatEnabled}
						InputProps={{
							endAdornment: <InputAdornment position="end">minutes</InputAdornment>
						}}
						{...register("overstay_duration")}
					/>
				</Grid>

				<Grid item xs={12} md={3}>
					<InputLabel
						htmlFor="hour-range-input"
						disabled={!overstayAlertsEnabled || !tatEnabled}
						sx={{ color: "#3d3d3d", marginBottom: "0.5rem" }}
					>
						Hour Range
					</InputLabel>

					<NumberInput
						fullWidth
						positiveOnly
						disableSigned
						disableDecimal
						size="small"
						variant="outlined"
						id="hour-range-input"
						placeholder="Enter hour range"
						maxValue={168}
						disabled={!overstayAlertsEnabled || !tatEnabled}
						InputProps={{
							endAdornment: <InputAdornment position="end">hours</InputAdornment>
						}}
						{...register("overstay_day_range")}
					/>
				</Grid>
			</Grid>

			<Box>
				<LoadingButton
					disableElevation
					variant="contained"
					color="success"
					startIcon={<SaveIcon />}
					loading={editSocietyConfigLoading}
					disabled={!inputChanged}
					type="submit"
				>
					Save Changes
				</LoadingButton>
			</Box>
		</Box>
	);
};

export default AdvancedSettings;
